import React, { useState } from 'react';
import { DatePicker, Form, Input, Select, Typography } from 'antd';
import { useDataProvider, useNotify } from 'react-admin';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { Field, FormSection, NetorcaForm, useServerErrors } from '@src/components/Common/Form';
import { EntityFormRoute, useServiceOptions } from '@src/components/Common/Integrations';
import MainCard from '@src/components/Common/MainCard';
import DeleteButton from '@src/components/Common/DeleteButton';
import { resourceMapping } from '@src/providers/dataProvider';

const { Paragraph } = Typography;

const RESOURCE = 'serviceowner_messages';
const BASE_PATH = '/serviceowner_messages';

/**
 * Where this form came from and where it goes back to.
 *
 * Messages are read and written from the team page's Messages tab — the
 * resource has no page of its own in the navigation — so leaving the form
 * returns to that tab rather than to a bare list route nothing links to.
 */
export const MESSAGES_TAB = '/teams?tab=messages';

/** MessageSerializer.Meta.fields — anything else is a general error */
const MESSAGE_FIELDS = ['description', 'expiry_date', 'scopes'];

/** the scopes field takes API URLs rather than ids */
const scopeUrl = (resource: string, id: number) => `/v1/${resourceMapping[resource]}/${id}/`;

const MessageHelp = () => (
    <Typography>
        <Paragraph>
            A message is a notice you publish to the consumers of your services. They see the active
            ones on their dashboard and in the response to a submission.
        </Paragraph>
        <Paragraph>
            <ul>
                <li>The message text is what consumers read, so write it for them.</li>
                <li>
                    Scoping it to services shows it only to the teams that consume those services.
                    With no service picked, everyone consuming any of your services sees it.
                </li>
                <li>
                    An expiry date takes the message down on its own. Without one it stands until
                    you delete it.
                </li>
            </ul>
        </Paragraph>
    </Typography>
);

const MessageForm = ({ record }: { record?: any }) => {
    const [form] = Form.useForm();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    // react-admin's redirect pushes its argument as a bare pathname, so a tab
    // in the query string would arrive as part of the path and never be read
    const history = useHistory();
    const back = () => history.push(MESSAGES_TAB);
    const { apply, fieldErrors, clear } = useServerErrors(form, MESSAGE_FIELDS);
    const { options: serviceOptions, loading: servicesLoading } = useServiceOptions();
    const [saving, setSaving] = useState(false);

    const editing = !!record;
    const scopes: any[] = record?.scopes || [];

    /**
     * Team scopes travel back untouched. This form only offers services, but the
     * API takes the whole scope list on every save — so anything scoped to a
     * team elsewhere would be dropped by a save made here.
     */
    const teamScopes = scopes
        .filter((entry: any) => entry.type === 'Team')
        .map((entry: any) => scopeUrl('teams', entry.scope.id));

    const onFinish = (values: any) => {
        clear();
        setSaving(true);

        const payload = {
            description: values.description,
            expiry_date: values.expiry_date ? dayjs(values.expiry_date).toISOString() : null,
            scopes: [
                ...teamScopes,
                ...(values.services || []).map((id: number) => scopeUrl('services', id)),
            ],
        };

        const request = editing
            ? dataProvider.update(RESOURCE, { id: record.id, data: payload, previousData: { id: record.id } })
            : dataProvider.create(RESOURCE, { data: payload });

        request
            .then(() => {
                notify(editing ? 'Message updated' : 'Message created');
                back();
            })
            .catch((error: any) => {
                apply(error, editing ? 'Could not update the message' : 'Could not create the message');
                setSaving(false);
            });
    };

    return (
        <NetorcaForm
            className="nf-form--sheet"
            form={form}
            name="message"
            title={editing ? `Message #${record.id}` : 'New message'}
            onBack={back}
            backLabel="Back to messages"
            help={{ title: 'How messages work', content: <MessageHelp /> }}
            submitLabel={editing ? 'Save changes' : 'Publish message'}
            submitting={saving}
            onCancel={back}
            onFinish={onFinish}
            serverErrors={fieldErrors}
            destructive={
                editing ? (
                    <DeleteButton
                        title="message"
                        resource={RESOURCE}
                        record={record}
                        redirectTo={MESSAGES_TAB}
                    />
                ) : undefined
            }
            initialValues={{
                description: record?.description,
                expiry_date: record?.expiry_date ? dayjs(record.expiry_date) : undefined,
                services: scopes
                    .filter((entry: any) => entry.type === 'Service')
                    .map((entry: any) => entry.scope.id),
            }}
        >
            {/* What the message says first — it is the whole point of the record —
                then who sees it and for how long, which share the row under it. */}
            <FormSection id="message" title="Message" bare>
                <Field
                    name="description"
                    label="Message"
                    rules={[{ required: true, message: 'Write the message consumers will read' }]}
                >
                    <Input.TextArea rows={4} placeholder="Scheduled maintenance on Saturday from 09:00 UTC" />
                </Field>

                <Field
                    name="services"
                    label="Services"
                    span={6}
                    hint="Only the teams consuming these services see it. Leave empty for every consumer of your services."
                >
                    <Select
                        mode="multiple"
                        allowClear
                        placeholder="All your services"
                        options={serviceOptions}
                        loading={servicesLoading}
                        showSearch={{ optionFilterProp: 'label' }}
                        maxTagCount="responsive"
                    />
                </Field>

                <Field
                    name="expiry_date"
                    label="Expires"
                    span={6}
                    hint="The message stops being shown at this time. Leave empty and it stands until you delete it."
                >
                    <DatePicker
                        showTime
                        style={{ width: '100%' }}
                        disabledDate={(current: any) => current && current < dayjs().startOf('day')}
                    />
                </Field>
            </FormSection>
        </NetorcaForm>
    );
};

const MessageEdit = (props: any) => (
    <EntityFormRoute
        id={props.id}
        resource={RESOURCE}
        basePath={BASE_PATH}
        render={(record) => (
            <MainCard>
                <MessageForm record={record} />
            </MainCard>
        )}
    />
);

export default MessageEdit;
